import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, inr } from "@/lib/api";
import { CheckCircle2, Package, Truck, Home as HomeIcon, Clock } from "lucide-react";

const STAGES = [
  { id: "placed", label: "Placed", icon: CheckCircle2 },
  { id: "packed", label: "Packed", icon: Package },
  { id: "out_for_delivery", label: "Out for delivery", icon: Truck },
  { id: "delivered", label: "Delivered", icon: HomeIcon },
];

export default function OrderTracking() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);

  useEffect(() => {
    api.get(`/orders/${id}`).then(({ data }) => setOrder(data));
  }, [id]);

  if (!order) return <div className="max-w-3xl mx-auto px-5 py-20 text-neutral-400">Loading…</div>;

  const stageIndex = STAGES.findIndex((s) => s.id === order.status);

  return (
    <div className="max-w-4xl mx-auto px-5 lg:px-8 py-10" data-testid="order-tracking-page">
      <p className="vm-overline">Order tracking</p>
      <div className="flex items-end justify-between gap-3 mt-2">
        <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tighter">Thanks for your order!</h1>
        <span className="vm-chip vm-chip-success">{order.status.replace(/_/g, " ").toUpperCase()}</span>
      </div>
      <p className="text-neutral-400 mt-2 font-mono text-sm">{order.order_id}</p>

      <div className="vm-card p-6 mt-8">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-volt-500/20 flex items-center justify-center">
            <Clock className="h-5 w-5 text-volt-400" />
          </div>
          <div>
            <p className="text-sm text-neutral-400">Estimated delivery</p>
            <p className="font-display text-2xl font-bold">~{order.eta_minutes} minutes</p>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-4 gap-2 relative">
          <div className="absolute top-5 left-5 right-5 h-0.5 bg-white/10" />
          <div
            className="absolute top-5 left-5 h-0.5 bg-volt-500 transition-all"
            style={{ width: `${(stageIndex / (STAGES.length - 1)) * 100}%` }}
          />
          {STAGES.map((s, i) => {
            const Icon = s.icon;
            const active = i <= stageIndex;
            return (
              <div key={s.id} className="flex flex-col items-center text-center z-10">
                <div className={`h-11 w-11 rounded-full flex items-center justify-center border-2 ${
                  active ? "bg-volt-500 border-volt-500 shadow-[0_0_18px_rgba(0,85,255,0.45)]" : "bg-ink-900 border-white/10"
                }`}>
                  <Icon className={`h-5 w-5 ${active ? "text-white" : "text-neutral-500"}`} />
                </div>
                <p className={`text-xs mt-2 ${active ? "text-white font-semibold" : "text-neutral-500"}`}>{s.label}</p>
              </div>
            );
          })}
        </div>
      </div>

      <div className="vm-card p-6 mt-6">
        <p className="vm-overline">Items</p>
        <div className="mt-4 space-y-3">
          {order.items.map((it) => (
            <div key={it.product_id} className="flex items-center gap-3">
              <img src={it.image} alt={it.name} className="h-14 w-14 rounded-lg object-cover bg-ink-800" />
              <div className="flex-1">
                <p className="text-xs text-neutral-500 uppercase tracking-widest font-bold">{it.brand}</p>
                <p className="text-sm font-medium">{it.name}</p>
              </div>
              <p className="font-mono text-sm">× {it.quantity}</p>
              <p className="font-mono text-sm w-24 text-right">{inr(it.price * it.quantity)}</p>
            </div>
          ))}
        </div>
        <div className="vm-divider my-5" />
        <div className="space-y-2 text-sm">
          <div className="flex justify-between"><span className="text-neutral-400">Subtotal</span><span className="font-mono">{inr(order.subtotal)}</span></div>
          <div className="flex justify-between"><span className="text-neutral-400">Delivery</span><span className="font-mono">{order.delivery_fee === 0 ? "FREE" : inr(order.delivery_fee)}</span></div>
          <div className="flex justify-between text-lg pt-2"><span className="font-semibold">Total (COD)</span><span className="font-display font-bold">{inr(order.total)}</span></div>
        </div>
      </div>

      <div className="vm-card p-6 mt-6">
        <p className="vm-overline">Delivery address</p>
        <p className="mt-3 text-sm leading-relaxed">
          <span className="font-semibold">{order.address.full_name}</span><br />
          {order.address.line1}, {order.address.city} - {order.address.pincode}<br />
          <span className="text-neutral-400">Phone: {order.address.phone}</span>
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3 mt-8">
        <Link to="/shop" className="vm-btn-ghost">Continue shopping</Link>
        <Link to="/profile" className="vm-btn-primary">View all orders</Link>
      </div>
    </div>
  );
}
